// Étalonnage final : reçoit le rendu HDR linéaire et produit l'image
// affichée. Remplace l'ancienne chaîne vignette + gamma, qui ignorait le
// tone mapping du renderer (ciel brûlé, image plate).

uniform sampler2D tDiffuse;
uniform vec2 resolution;
uniform float time;

uniform float exposure;
uniform float contrast;
uniform float saturation;
uniform vec3 shadowTint;
uniform vec3 highlightTint;
uniform float splitToneStrength;

uniform float offset;
uniform float darkness;

uniform float grain;

varying vec2 vUv;

// ACES "fitted" (S. Hill) : épaule douce, mêmes coefficients que three.js
vec3 gradeRrtOdtFit(vec3 v) {
    vec3 a = v * (v + 0.0245786) - 0.000090537;
    vec3 b = v * (0.983729 * v + 0.4329510) + 0.238081;
    return a / b;
}

vec3 acesFitted(vec3 color) {
    const mat3 ACESInputMat = mat3(
        vec3(0.59719, 0.07600, 0.02840),
        vec3(0.35458, 0.90834, 0.13383),
        vec3(0.04823, 0.01566, 0.83777)
    );
    const mat3 ACESOutputMat = mat3(
        vec3( 1.60475, -0.10208, -0.00327),
        vec3(-0.53108,  1.10813, -0.07276),
        vec3(-0.07367, -0.00605,  1.07602)
    );

    color = ACESInputMat * color;
    color = gradeRrtOdtFit(color);
    color = ACESOutputMat * color;
    return clamp(color, 0.0, 1.0);
}

vec3 gradeLinearToSRGB(vec3 c) {
    return mix(
        c * 12.92,
        1.055 * pow(c, vec3(1.0 / 2.4)) - 0.055,
        step(0.0031308, c)
    );
}

float hash12(vec2 p) {
    vec3 p3 = fract(vec3(p.xyx) * 0.1031);
    p3 += dot(p3, p3.yzx + 33.33);
    return fract((p3.x + p3.y) * p3.z);
}

void main() {
    vec3 color = texture2D(tDiffuse, vUv).rgb;

    // ---------------------------------------------------------------
    // Tone mapping (domaine linéaire)
    // ---------------------------------------------------------------
    color *= exposure / 0.6;
    color = acesFitted(color);
    color = gradeLinearToSRGB(color);

    // ---------------------------------------------------------------
    // Étalonnage (domaine affiché)
    // ---------------------------------------------------------------
    float luma = dot(color, vec3(0.2126, 0.7152, 0.0722));

    // Saturation retenue : évite les verts acides, garde de la densité
    color = mix(vec3(luma), color, saturation);

    // Contraste en S autour du gris moyen
    color = mix(vec3(0.5), color, contrast);
    color = clamp(color, 0.0, 1.0);

    // Split toning : ombres légèrement froides, hautes lumières chaudes
    luma = dot(color, vec3(0.2126, 0.7152, 0.0722));
    float shadowW = 1.0 - smoothstep(0.0, 0.55, luma);
    float highlightW = smoothstep(0.45, 1.0, luma);
    color += (shadowTint - 0.5) * shadowW * splitToneStrength;
    color += (highlightTint - 0.5) * highlightW * splitToneStrength;

    // ---------------------------------------------------------------
    // Vignette optique (elliptique, corrigée du ratio)
    // ---------------------------------------------------------------
    vec2 centered = vUv - 0.5;
    centered.x *= mix(1.0, resolution.x / resolution.y, 0.5);
    float dist = length(centered);
    float vignette = smoothstep(offset, offset + 0.75, dist);
    color *= 1.0 - vignette * vignette * darkness * 0.32;

    // ---------------------------------------------------------------
    // Grain argentique fin + dithering (supprime le banding du ciel)
    // ---------------------------------------------------------------
    vec2 px = vUv * resolution;
    float t = fract(time * 0.0137) * 97.0;
    float n = hash12(px + t) + hash12(px * 1.37 - t) - 1.0;
    float grainMask = 1.0 - abs(luma - 0.5) * 1.2;
    color += n * grain * grainMask;
    color += (hash12(px + 17.0) - 0.5) / 255.0;

    gl_FragColor = vec4(clamp(color, 0.0, 1.0), 1.0);
}
